import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { CWY_COLORS } from '../../theme/colors';
import { ChatPaymentDetailsSheet } from './ChatPaymentDetailsSheet';
import {
  chatPaymentFooterLabel,
  formatPaymentAmount,
  payeeLabel,
  resolveChatPaymentDisplayStatus,
  sentToLabel,
} from '../../utils/chatPaymentDisplay';

export interface ChatPaymentBubbleProps {
  amount: number;
  currency: string;
  metadata: Record<string, unknown>;
  content?: string;
  timestampLabel?: string;
  isOutgoing?: boolean;
  isPaying?: boolean;
  onPay?: () => void;
  /** Auto-open details + celebrate when payment just completed */
  autoOpenDetails?: boolean;
  onDetailsAutoOpened?: () => void;
}

export function ChatPaymentBubble({
  amount,
  currency,
  metadata,
  content,
  timestampLabel,
  isOutgoing = false,
  isPaying = false,
  onPay,
  autoOpenDetails = false,
  onDetailsAutoOpened,
}: ChatPaymentBubbleProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const display = resolveChatPaymentDisplayStatus(
    metadata.payment_status as string | undefined,
    metadata.expires_at as string | undefined
  );
  const canPay = display === 'pay' && Boolean(metadata.transaction_id) && Boolean(onPay);
  const isCompleted = display === 'completed';
  const isExpired = display === 'expired';

  useEffect(() => {
    if (!autoOpenDetails) return;
    setDetailsOpen(true);
    setCelebrate(true);
    onDetailsAutoOpened?.();
  }, [autoOpenDetails, onDetailsAutoOpened]);

  const openDetails = () => {
    setCelebrate(isCompleted);
    setDetailsOpen(true);
  };

  const heroBg = isCompleted
    ? 'linear-gradient(165deg, rgba(30,157,99,0.08) 0%, rgba(227,242,253,0.55) 55%, #ffffff 100%)'
    : isExpired
      ? 'linear-gradient(165deg, #f3f4f6 0%, #fafafa 100%)'
      : 'linear-gradient(165deg, rgba(204,27,27,0.05) 0%, rgba(227,242,253,0.45) 50%, #ffffff 100%)';

  const iconBg = isCompleted
    ? CWY_COLORS.verified
    : isExpired
      ? '#9e9e9e'
      : CWY_COLORS.successGreen;

  return (
    <>
      <Box
        onClick={openDetails}
        sx={{
          maxWidth: '100%',
          minWidth: 228,
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: '#ffffff',
          border: '1px solid',
          borderColor: isCompleted
            ? 'rgba(30,157,99,0.28)'
            : isExpired
              ? 'rgba(0,0,0,0.1)'
              : 'rgba(0,0,0,0.08)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderTopRightRadius: isOutgoing ? 6 : 18,
          borderTopLeftRadius: isOutgoing ? 18 : 6,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2.5,
            textAlign: 'center',
            background: heroBg,
            borderBottom: '1px solid',
            borderColor: 'rgba(0,0,0,0.06)',
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.7rem',
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              color: isExpired ? CWY_COLORS.textSecondary : '#1a237e',
            }}
          >
            {formatPaymentAmount(amount, currency)}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.75,
            py: 1.35,
            bgcolor: '#ffffff',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.1,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              bgcolor: iconBg,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mb: 0.15,
            }}
          >
            {isCompleted ? (
              <CheckRoundedIcon sx={{ fontSize: 18 }} />
            ) : isExpired ? (
              <ScheduleRoundedIcon sx={{ fontSize: 17 }} />
            ) : (
              <PaymentsRoundedIcon sx={{ fontSize: 17 }} />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: CWY_COLORS.textSecondary,
                fontSize: '0.78rem',
                lineHeight: 1.3,
              }}
            >
              {isCompleted ? sentToLabel(metadata) : payeeLabel(metadata)}
            </Typography>

            {canPay ? (
              <Button
                size="small"
                variant="contained"
                disabled={isPaying}
                onClick={(e) => {
                  e.stopPropagation();
                  onPay?.();
                }}
                sx={{
                  mt: 0.55,
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: 76,
                  height: 30,
                  borderRadius: 999,
                  bgcolor: CWY_COLORS.successGreen,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#006400', boxShadow: 'none' },
                }}
              >
                {isPaying ? <CircularProgress size={14} color="inherit" /> : 'Pay'}
              </Button>
            ) : (
              <Typography
                sx={{
                  mt: 0.15,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: isCompleted ? CWY_COLORS.verified : CWY_COLORS.textFaint,
                  lineHeight: 1.3,
                }}
              >
                {chatPaymentFooterLabel(display)}
              </Typography>
            )}
          </Box>

          {timestampLabel ? (
            <Typography
              variant="caption"
              sx={{
                color: CWY_COLORS.textFaint,
                fontSize: 11,
                flexShrink: 0,
                alignSelf: 'flex-end',
                pb: 0.15,
              }}
            >
              {timestampLabel}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <ChatPaymentDetailsSheet
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setCelebrate(false);
        }}
        amount={amount}
        currency={currency}
        metadata={metadata}
        content={content}
        timestampLabel={timestampLabel}
        celebrateSuccess={celebrate}
      />
    </>
  );
}
